import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, CircularProgress, Chip, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneIcon from '@mui/icons-material/Phone';
import FlagIcon from '@mui/icons-material/Flag';
import NotesIcon from '@mui/icons-material/Notes';
import BadgeIcon from '@mui/icons-material/Badge';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getConsultationById } from '../../../api/consultation';
import { formatVisitPath } from '../../../constants/visitPath';
import {
  getConsultMethodLabel,
  getConsultPurposeLabel,
  getConsultResultLabel,
  CONSULT_RESULT_COLORS,
} from '../../../constants/consultation';
import useUserAuth from '../../../utils/useUserAuth';
import EnterSaleInfoModal from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModal';

// 스타일 컴포넌트
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '520px',
  maxHeight: '650px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  background: '#262626',
});

const ModalBody = styled(Box)({
  padding: '24px',
  maxHeight: '480px',
  overflowY: 'auto',
  backgroundColor: '#fafafa',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '60px',
});

const SectionCard = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '10px',
  padding: '16px 20px',
  marginBottom: '16px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  border: '1px solid #eee',
});

const SectionTitle = styled(Typography)({
  fontSize: '12px',
  fontWeight: 600,
  color: '#262626',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: '1px solid #f5f5f5',
  '&:last-child': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
  '&:first-of-type': {
    paddingTop: 0,
  },
});

const InfoIcon = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '12px',
  '& svg': {
    fontSize: '16px',
    color: '#262626',
  },
});

const InfoLabel = styled(Typography)({
  width: '80px',
  flexShrink: 0,
  fontSize: '13px',
  fontWeight: 500,
  color: '#888',
});

const InfoValue = styled(Typography)({
  flex: 1,
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
});

const ContentBox = styled(Box)({
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  padding: '14px 16px',
  minHeight: '100px',
  fontSize: '14px',
  color: '#444',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.6,
  border: '1px solid #eee',
});

const ModalFooter = styled(Box)({
  display: 'flex',
  gap: '10px',
  padding: '16px 24px',
  backgroundColor: '#fff',
  borderTop: '1px solid #eee',
  justifyContent: 'flex-end',
});

const StyledChip = styled(Chip)({
  height: '24px',
  fontSize: '12px',
  fontWeight: 500,
});



// consultType별 이름 표시
const getDisplayName = row => {
  if (!row) return '-';
  if (row.consultType === 'MEMBER') return row.memberName || '-';
  if (row.consultType === 'TEMPORARY') return row.name ? `${row.name} (잠재)` : '-';
  if (row.consultType === 'NON_MEMBER') return row.name ? `${row.name} (비회원)` : '-';
  return row.memberName || row.name || '-';
};

// 시간 포맷팅
const formatTime = timeValue => {
  if (!timeValue) return '';

  // 문자열인 경우 (예: "09:00", "09:00:00")
  if (typeof timeValue === 'string') {
    const parts = timeValue.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return timeValue;
  }

  // 객체인 경우 (예: {hour: 9, minute: 0})
  if (typeof timeValue === 'object') {
    const hour = timeValue.hour ?? timeValue.hours ?? 0;
    const minute = timeValue.minute ?? timeValue.minutes ?? 0;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  return '';
};

/**
 * 상담 상세 모달 컴포넌트
 */
const ConsultationDetailModal = ({ open, onClose, consultationData, onEdit }) => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const canSaleRegister = useUserAuth('MEMBER_SALE_REGISTER');
  const [saleModalOpen, setSaleModalOpen] = useState(false);

  // 판매등록 대상 (비회원 상담은 판매 대상이 없다)
  const saleUserInfo =
    consultation?.consultType === 'MEMBER' && consultation?.memberId
      ? { userId: consultation.memberId, userType: 'member' }
      : consultation?.consultType === 'TEMPORARY' && consultation?.temporaryUserId
        ? { userId: consultation.temporaryUserId, userType: 'temporary' }
        : null;

  // EnterSaleInfoModal 하위에서 쓰는 필드는 memberId·name·phoneNumber 뿐이라 상담 응답으로 구성한다
  const saleUserDetail = consultation
    ? {
        memberId: consultation.memberId,
        name: consultation.memberName || consultation.name || '',
        phoneNumber: consultation.phone,
      }
    : null;

  // 회원/잠재회원 상세 경로 (비회원은 이동할 상세가 없다)
  const detailPath =
    consultation?.consultType === 'MEMBER' && consultation?.memberId
      ? `/member/${consultation.memberId}`
      : consultation?.consultType === 'TEMPORARY' && consultation?.temporaryUserId
        ? `/temporary/${consultation.temporaryUserId}`
        : null;

  // 모달이 열릴 때 API 호출하여 상담 정보 가져오기
  useEffect(() => {
    const fetchConsultation = async () => {
      if (!open || !consultationData?.consultationId) {
        return;
      }

      setIsLoading(true);
      try {
        const found = await getConsultationById(consultationData.consultationId, user.token);
        setConsultation(found || null);
      } catch (error) {
        console.error('상담 정보 조회 실패:', error);
        setConsultation(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConsultation();
  }, [open, consultationData?.consultationId, user?.token]);

  // 모달 닫을 때 상태 초기화
  useEffect(() => {
    if (!open) {
      setConsultation(null);
      setSaleModalOpen(false);
    }
  }, [open]);

  if (!consultationData) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        {/* 헤더 */}
        <ModalHeader>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '18px' }}>상담 상세</Typography>
          </Box>
          <CloseIcon
            sx={{
              cursor: 'pointer',
              color: '#fff',
              fontSize: '28px',
              padding: '4px',
              borderRadius: '50%',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
            }}
            onClick={onClose}
          />
        </ModalHeader>

        {/* 바디 */}
        <ModalBody>
          {isLoading ? (
            <LoadingContainer>
              <CircularProgress sx={{ color: '#4AB3BC' }} />
            </LoadingContainer>
          ) : consultation ? (
            <>
              {/* 기본 정보 섹션 */}
              <SectionCard>
                <SectionTitle>
                  <NotesIcon sx={{ fontSize: '14px' }} />
                  기본 정보
                </SectionTitle>
                <InfoRow>
                  <InfoIcon>
                    <NotesIcon />
                  </InfoIcon>
                  <InfoLabel>상담명</InfoLabel>
                  <InfoValue>{consultation.title || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoIcon>
                    <CalendarTodayIcon />
                  </InfoIcon>
                  <InfoLabel>일시</InfoLabel>
                  <InfoValue>
                    {consultation.consultDate || '-'}
                    {(consultation.consultStartTime || consultation.consultEndTime) && (
                      <Typography component="span" sx={{ color: '#4AB3BC', fontWeight: 600, ml: 1 }}>
                        {formatTime(consultation.consultStartTime)} - {formatTime(consultation.consultEndTime)}
                      </Typography>
                    )}
                  </InfoValue>
                </InfoRow>
              </SectionCard>

              {/* 참여자 정보 섹션 */}
              <SectionCard>
                <SectionTitle>
                  <PersonIcon sx={{ fontSize: '14px' }} />
                  참여자 정보
                </SectionTitle>
                <InfoRow>
                  <InfoIcon>
                    <BadgeIcon />
                  </InfoIcon>
                  <InfoLabel>담당자</InfoLabel>
                  <InfoValue>{consultation.managerName || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoIcon>
                    <PersonIcon />
                  </InfoIcon>
                  <InfoLabel>회원</InfoLabel>
                  {/* 회원·잠재회원은 상세로 바로 이동. 비회원은 갈 곳이 없어 텍스트로 둔다. */}
                  {detailPath ? (
                    <InfoValue
                      onClick={() => navigate(detailPath)}
                      sx={{
                        fontWeight: 600,
                        color: '#0F7F85',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}>
                      {getDisplayName(consultation)}
                    </InfoValue>
                  ) : (
                    <InfoValue sx={{ fontWeight: 600 }}>{getDisplayName(consultation)}</InfoValue>
                  )}
                </InfoRow>
              </SectionCard>

              {/* 상담 정보 섹션 */}
              <SectionCard>
                <SectionTitle>
                  <PhoneIcon sx={{ fontSize: '14px' }} />
                  상담 정보
                </SectionTitle>
                <InfoRow>
                  <InfoIcon>
                    <FlagIcon />
                  </InfoIcon>
                  <InfoLabel>상담 결과</InfoLabel>
                  <InfoValue>
                    <StyledChip
                      label={getConsultResultLabel(consultation.consultResult || 'SCHEDULED')}
                      sx={{
                        backgroundColor: CONSULT_RESULT_COLORS[consultation.consultResult || 'SCHEDULED'],
                        color: '#fff',
                      }}
                    />
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoIcon>
                    <PhoneIcon />
                  </InfoIcon>
                  <InfoLabel>상담 수단</InfoLabel>
                  <InfoValue>
                    <StyledChip
                      label={getConsultMethodLabel(consultation.consultMethod)}
                      sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                    />
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoIcon>
                    <FlagIcon />
                  </InfoIcon>
                  <InfoLabel>상담 목적</InfoLabel>
                  <InfoValue>
                    <StyledChip
                      label={getConsultPurposeLabel(consultation.consultPurpose)}
                      sx={{ backgroundColor: '#fff3e0', color: '#e65100' }}
                    />
                  </InfoValue>
                </InfoRow>
                {consultation.visitPath && (
                  <InfoRow>
                    <InfoIcon>
                      <FlagIcon />
                    </InfoIcon>
                    <InfoLabel>방문경로</InfoLabel>
                    <InfoValue>
                      <StyledChip
                        label={formatVisitPath(consultation.visitPath, consultation.visitPathEtc)}
                        sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                      />
                    </InfoValue>
                  </InfoRow>
                )}
              </SectionCard>

              {/* 상담 내용 섹션 */}
              <SectionCard sx={{ mb: 0 }}>
                <SectionTitle>
                  <NotesIcon sx={{ fontSize: '14px' }} />
                  상담 내용
                </SectionTitle>
                <ContentBox>{consultation.content || '상담 내용이 없습니다.'}</ContentBox>
              </SectionCard>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: '#999', fontSize: '15px' }}>상담 정보를 불러올 수 없습니다.</Typography>
            </Box>
          )}
        </ModalBody>

        {/* 푸터 */}
        <ModalFooter>
          <Button
            variant="outlined"
            sx={{
              borderColor: '#ddd',
              color: '#666',
              borderRadius: '8px',
              px: 3,
              '&:hover': { borderColor: '#bbb', backgroundColor: '#f9f9f9' },
            }}
            onClick={onClose}>
            {t('닫기')}
          </Button>
          {saleUserInfo && (
            <Tooltip title={!canSaleRegister ? '권한이 없습니다' : ''} arrow>
              <span>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#4AB3BC',
                    color: '#0F7F85',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': { borderColor: '#3a9ca5', backgroundColor: 'rgba(74, 179, 188, 0.08)' },
                  }}
                  onClick={() => setSaleModalOpen(true)}
                  disabled={!canSaleRegister}>
                  {t('판매 등록')}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#4AB3BC',
              borderRadius: '8px',
              px: 3,
              boxShadow: '0 2px 8px rgba(74, 179, 188, 0.3)',
              '&:hover': { backgroundColor: '#3a9ca5' },
            }}
            onClick={() => onEdit(consultation)}
            disabled={!consultation}>
            {t('상담 수정')}
          </Button>
        </ModalFooter>

        {/* 판매등록 모달 — MUI Modal 은 포털로 뜨므로 중첩 마운트해도 무방하다 */}
        {saleUserInfo && saleModalOpen && (
          <EnterSaleInfoModal
            userInfo={saleUserInfo}
            userDetail={saleUserDetail}
            open={saleModalOpen}
            onOpen={setSaleModalOpen}
            onClose={() => setSaleModalOpen(false)}
            hideButton={true}
            fetchData={async () => {}}
          />
        )}
      </ModalContainer>
    </Modal>
  );
};

export default ConsultationDetailModal;
